import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './store/authStore';
import { AppShell } from './components/AppShell';
import { LoadingOrb } from './components/LoadingOrb';
import { PageTransition } from './components/PageTransition';
import { SetupProvider } from './store/setupStore';
import { AlertToastProvider } from './store/alertToastStore';
import { SuccessToastProvider } from './store/successToastStore';
// Lazy loaded components
const SplashScreen = lazy(() => import('./pages/SplashScreen').then(m => ({
  default: m.SplashScreen
})));
const IntroSlides = lazy(() => import('./pages/IntroSlides').then(m => ({
  default: m.IntroSlides
})));
const RegisterScreen = lazy(() => import('./pages/RegisterScreen').then(m => ({
  default: m.RegisterScreen
})));
const LoginScreen = lazy(() => import('./pages/LoginScreen').then(m => ({
  default: m.LoginScreen
})));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({
  default: m.ForgotPassword
})));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({
  default: m.ResetPassword
})));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({
  default: m.Dashboard
})));
const MonitorScreen = lazy(() => import('./pages/MonitorScreen').then(m => ({
  default: m.MonitorScreen
})));
const SessionsList = lazy(() => import('./pages/history/SessionsList').then(m => ({
  default: m.SessionsList
})));
const SessionDetail = lazy(() => import('./pages/history/SessionDetail').then(m => ({
  default: m.SessionDetail
})));
const ShareReport = lazy(() => import('./pages/history/ShareReport').then(m => ({
  default: m.ShareReport
})));
const AppointmentsList = lazy(() => import('./pages/appointments/AppointmentsList').then(m => ({
  default: m.AppointmentsList
})));
const LearnHome = lazy(() => import('./pages/learn/LearnHome').then(m => ({
  default: m.LearnHome
})));
const LearnSection = lazy(() => import('./pages/learn/LearnSection').then(m => ({
  default: m.LearnSection
})));
const MotherScreen = lazy(() => import('./pages/mother/MotherScreen').then(m => ({
  default: m.MotherScreen
})));
const BabyScreen = lazy(() => import('./pages/baby/BabyScreen').then(m => ({
  default: m.BabyScreen
})));
const AuroraChat = lazy(() => import('./pages/aurora/AuroraChat').then(m => ({
  default: m.AuroraChat
})));
const AuroraHistory = lazy(() => import('./pages/aurora/AuroraHistory').then(m => ({
  default: m.AuroraHistory
})));
const AuroraConversationView = lazy(() => import('./pages/aurora/AuroraConversationView').then(m => ({
  default: m.AuroraConversationView
})));
const AlertsList = lazy(() => import('./pages/alerts/AlertsList').then(m => ({
  default: m.AlertsList
})));
const AlertDetail = lazy(() => import('./pages/alerts/AlertDetail').then(m => ({
  default: m.AlertDetail
})));
const ProfileScreen = lazy(() => import('./pages/profile/ProfileScreen').then(m => ({
  default: m.ProfileScreen
})));
const EditProfile = lazy(() => import('./pages/profile/EditProfile').then(m => ({
  default: m.EditProfile
})));
const DeviceManagement = lazy(() => import('./pages/profile/DeviceManagement').then(m => ({
  default: m.DeviceManagement
})));
const DeviceEntry = lazy(() => import('./pages/setup/DeviceEntry').then(m => ({
  default: m.DeviceEntry
})));
const WaitingActivation = lazy(() => import('./pages/setup/WaitingActivation').then(m => ({
  default: m.WaitingActivation
})));
const SetupSuccess = lazy(() => import('./pages/setup/SetupSuccess').then(m => ({
  default: m.SetupSuccess
})));
// Route Guard for unauthenticated users trying to access protected routes
function ProtectedRoute({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated,
    isLoading
  } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return <FullScreenLoader data-id="element-0" />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/splash" state={{
      from: location
    }} replace data-id="element-1" />;
  }
  return <>{children}</>;
}
// Route Guard for authenticated users trying to access auth screens
function AuthRoute({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated,
    isLoading
  } = useAuth();
  if (isLoading) {
    return <FullScreenLoader data-id="element-2" />;
  }
  if (isAuthenticated) {
    return <Navigate to="/app/dashboard" replace data-id="element-3" />;
  }
  return <>{children}</>;
}
function FullScreenLoader() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const startTime = Date.now();
    const duration = 1500;
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      setProgress(eased * 100);
      if (t < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, []);
  return <div className="min-h-screen w-full bg-secondary flex flex-col items-center justify-center" data-id="element-4">
      <LoadingOrb data-id="element-5" />
      <h1 className="font-sans text-[28px] font-semibold text-text-primary tracking-[4px] uppercase mt-6 mb-6" data-id="element-6">
        Aurora Care
      </h1>
      <div className="w-[120px] h-[3px] bg-border rounded-pill overflow-hidden" data-id="element-7">
        <div className="h-full bg-primary rounded-pill transition-none" style={{
        width: `${progress}%`
      }} data-id="element-8" />
      </div>
    </div>;
}
function AppRoutes() {
  return <Suspense fallback={<FullScreenLoader data-id="element-10" />} data-id="element-9">
      <Routes data-id="element-11">
        {/* Public / Auth Routes */}
        <Route path="/" element={<Navigate to="/splash" replace data-id="element-13" />} data-id="element-12" />
        <Route path="/splash" element={<AuthRoute data-id="element-15">
              <PageTransition type="fade" data-id="element-16">
                <SplashScreen data-id="element-17" />
              </PageTransition>
            </AuthRoute>} data-id="element-14" />
        <Route path="/intro" element={<AuthRoute data-id="element-19">
              <PageTransition type="fade" data-id="element-20">
                <IntroSlides data-id="element-21" />
              </PageTransition>
            </AuthRoute>} data-id="element-18" />
        <Route path="/register" element={<AuthRoute data-id="element-23">
              <PageTransition type="slideUp" data-id="element-24">
                <RegisterScreen data-id="element-25" />
              </PageTransition>
            </AuthRoute>} data-id="element-22" />
        <Route path="/login" element={<AuthRoute data-id="element-27">
              <PageTransition type="slideUp" data-id="element-28">
                <LoginScreen data-id="element-29" />
              </PageTransition>
            </AuthRoute>} data-id="element-26" />
        <Route path="/forgot-password" element={<AuthRoute data-id="element-31">
              <PageTransition type="slideUp" data-id="element-32">
                <ForgotPassword data-id="element-33" />
              </PageTransition>
            </AuthRoute>} data-id="element-30" />
        <Route path="/reset-password" element={<PageTransition type="slideUp" data-id="element-35">
              <ResetPassword data-id="element-36" />
            </PageTransition>} data-id="element-34" />

        {/* Protected Routes */}
        <Route path="/app/*" element={<ProtectedRoute data-id="element-38">
              <DashboardRoutes data-id="element-39" />
            </ProtectedRoute>} data-id="element-37" />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/splash" replace data-id="element-41" />} data-id="element-40" />
      </Routes>
    </Suspense>;
}
function DashboardRoutes() {
  return <Suspense fallback={<FullScreenLoader data-id="element-43" />} data-id="element-42">
      <Routes data-id="element-44">
        {/* Setup flow doesn't use the AppShell */}
        <Route path="setup/*" element={<SetupProvider data-id="element-46">
              <Routes data-id="element-47">
                <Route path="" element={<PageTransition type="slideUp" data-id="element-49">
                      <DeviceEntry data-id="element-50" />
                    </PageTransition>} data-id="element-48" />
                <Route path="waiting" element={<PageTransition type="slideRight" data-id="element-52">
                      <WaitingActivation data-id="element-53" />
                    </PageTransition>} data-id="element-51" />
                <Route path="success" element={<PageTransition type="slideUp" data-id="element-55">
                      <SetupSuccess data-id="element-56" />
                    </PageTransition>} data-id="element-54" />
              </Routes>
            </SetupProvider>} data-id="element-45" />

        {/* Monitor screen is full screen, outside AppShell */}
        <Route path="monitor" element={<PageTransition type="slideUp" data-id="element-58">
              <MonitorScreen data-id="element-59" />
            </PageTransition>} data-id="element-57" />

        {/* All other app routes use the AppShell */}
        <Route path="*" element={<AppShell data-id="element-61">
              <Routes data-id="element-62">
                <Route path="dashboard" element={<PageTransition type="fade" data-id="element-64">
                      <Dashboard data-id="element-65" />
                    </PageTransition>} data-id="element-63" />
                <Route path="mother" element={<PageTransition type="fade" data-id="element-67">
                      <MotherScreen data-id="element-68" />
                    </PageTransition>} data-id="element-66" />
                <Route path="baby" element={<PageTransition type="fade" data-id="element-70">
                      <BabyScreen data-id="element-71" />
                    </PageTransition>} data-id="element-69" />
                <Route path="history" element={<PageTransition type="fade" data-id="element-73">
                      <SessionsList data-id="element-74" />
                    </PageTransition>} data-id="element-72" />
                <Route path="history/:sessionId" element={<PageTransition type="slideRight" data-id="element-76">
                      <SessionDetail data-id="element-77" />
                    </PageTransition>} data-id="element-75" />
                <Route path="history/:sessionId/share" element={<PageTransition type="slideRight" data-id="element-79">
                      <ShareReport data-id="element-80" />
                    </PageTransition>} data-id="element-78" />
                <Route path="appointments" element={<PageTransition type="fade" data-id="element-82">
                      <AppointmentsList data-id="element-83" />
                    </PageTransition>} data-id="element-81" />
                <Route path="learn" element={<PageTransition type="fade" data-id="element-85">
                      <LearnHome data-id="element-86" />
                    </PageTransition>} data-id="element-84" />
                <Route path="learn/:slug" element={<PageTransition type="slideRight" data-id="element-88">
                      <LearnSection data-id="element-89" />
                    </PageTransition>} data-id="element-87" />
                <Route path="aurora" element={<PageTransition type="fade" data-id="element-91">
                      <AuroraChat data-id="element-92" />
                    </PageTransition>} data-id="element-90" />
                <Route path="aurora/history" element={<PageTransition type="slideRight" data-id="element-94">
                      <AuroraHistory data-id="element-95" />
                    </PageTransition>} data-id="element-93" />
                <Route path="aurora/history/:conversationId" element={<PageTransition type="slideRight" data-id="element-97">
                      <AuroraConversationView data-id="element-98" />
                    </PageTransition>} data-id="element-96" />
                <Route path="alerts" element={<PageTransition type="fade" data-id="element-100">
                      <AlertsList data-id="element-101" />
                    </PageTransition>} data-id="element-99" />
                <Route path="alerts/:alertId" element={<PageTransition type="slideRight" data-id="element-103">
                      <AlertDetail data-id="element-104" />
                    </PageTransition>} data-id="element-102" />
                <Route path="profile" element={<PageTransition type="fade" data-id="element-106">
                      <ProfileScreen data-id="element-107" />
                    </PageTransition>} data-id="element-105" />
                <Route path="profile/edit" element={<PageTransition type="slideRight" data-id="element-109">
                      <EditProfile data-id="element-110" />
                    </PageTransition>} data-id="element-108" />
                <Route path="profile/devices" element={<PageTransition type="slideRight" data-id="element-112">
                      <DeviceManagement data-id="element-113" />
                    </PageTransition>} data-id="element-111" />
                <Route path="*" element={<Navigate to="dashboard" replace data-id="element-115" />} data-id="element-114" />
              </Routes>
            </AppShell>} data-id="element-60" />
      </Routes>
    </Suspense>;
}
export function App() {
  return <AuthProvider data-id="element-116">
      <AlertToastProvider data-id="element-117">
        <SuccessToastProvider data-id="element-118">
          <BrowserRouter data-id="element-119">
            <AppRoutes data-id="element-120" />
          </BrowserRouter>
        </SuccessToastProvider>
      </AlertToastProvider>
    </AuthProvider>;
}